import { PaasTransaction, SideDrawer, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import CategoryEditor from '../CategoryEditor/CategoryEditor';
import { formatAmount } from '../formatAmount';
import { filterRowsByCategory, formatTransactionDate, rowAmount } from '../insightsModel';
import type { CategoryDescriptor, CategoryKind } from '../categories';
import styles from './TransactionsDrawer.module.scss';

const cn = withStyles(styles);

type MergedRow = {
  id: string;
  date: string;
  classification: string;
  description: string;
  amount: number;
};

type TransactionsDrawerProps = {
  // 'income' or 'expense'; null keeps the drawer closed
  kind: Extract<CategoryKind, 'income' | 'expense'> | null;
  descriptors: CategoryDescriptor[];
  categories: CategoryDescriptor[];
  transactions: PaasTransaction[];
  months: string[];
  currency: string;
  onClose: () => void;
  onUpdateCategory: (transactionID: string, classification: string) => Promise<void>;
};

export default function TransactionsDrawer({
  kind,
  descriptors,
  categories,
  transactions,
  months,
  currency,
  onClose,
  onUpdateCategory,
}: TransactionsDrawerProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.Customers.PaymentMethodsTab.DataEnrichment.Drawer',
  });

  const side: 'credit' | 'debit' = kind === 'income' ? 'credit' : 'debit';
  const knownClassifications = new Set(categories.map((descriptor) => descriptor.classification));

  const categorizedRows: MergedRow[] = descriptors.flatMap((descriptor) =>
    filterRowsByCategory(transactions, descriptor, months).map((row) => ({
      id: row.AggregatorTransactionID,
      date: row.TransactionDate,
      classification: row.Classification,
      description: row.Description,
      amount: rowAmount(row, descriptor),
    })),
  );

  // Transactions of this side the enrichment could not classify — surfaced so
  // the user can categorize them (they are excluded from the charts until then)
  const uncategorizedRows: MergedRow[] = transactions
    .filter((row) => {
      const sideAmount = side === 'credit' ? row.Credit : row.Debit;

      return (
        sideAmount !== null &&
        !knownClassifications.has(row.Classification) &&
        months.includes(row.TransactionDate.slice(0, 7))
      );
    })
    .map((row) => ({
      id: row.AggregatorTransactionID,
      date: row.TransactionDate,
      classification: '',
      description: row.Description,
      amount: (side === 'credit' ? row.Credit : row.Debit) ?? 0,
    }));

  const mergedRows = [...categorizedRows, ...uncategorizedRows].sort((a, b) => b.date.localeCompare(a.date));

  const totalAmount = categorizedRows.reduce((sum, row) => sum + row.amount, 0);

  const title =
    kind === 'income'
      ? t('incomeTransactionsTitle', { defaultValue: 'Income Transactions' })
      : t('expenseTransactionsTitle', { defaultValue: 'Expense Transactions' });

  const summaryRows = [
    {
      label:
        kind === 'income'
          ? t('totalIncome', { defaultValue: 'Total Income' })
          : t('totalExpense', { defaultValue: 'Total Expense' }),
      value: formatAmount(totalAmount, currency),
    },
    {
      label: t('avgMonthly', { defaultValue: 'Avg. Monthly' }),
      value: formatAmount(months.length > 0 ? totalAmount / months.length : 0, currency),
    },
    { label: t('categories', { defaultValue: 'Categories' }), value: descriptors.length },
    { label: t('transactions', { defaultValue: 'Transactions' }), value: categorizedRows.length },
    ...(uncategorizedRows.length > 0
      ? [{ label: t('uncategorized', { defaultValue: 'Uncategorized' }), value: uncategorizedRows.length }]
      : []),
  ];

  return (
    <SideDrawer isOpen={kind !== null} onClose={onClose} title={title}>
      <div className={cn('container')}>
        <div className={cn('summaryCard')}>
          <div className={cn('summaryTitle')}>{t('summary', { defaultValue: 'Summary' })}</div>

          {summaryRows.map(({ label, value }) => (
            <div key={label} className={cn('summaryRow')}>
              <span className={cn('summaryLabel')}>{label}</span>
              <span className={cn('summaryValue')}>{value}</span>
            </div>
          ))}
        </div>

        <div className={cn('tableWrap')}>
          <div className={cn('tableHeader')}>
            <span>{t('columnDate', { defaultValue: 'Transaction Date' })}</span>
            <span>{t('columnCategory', { defaultValue: 'Category' })}</span>
            <span>{t('columnDescription', { defaultValue: 'Description' })}</span>
            <span>{t('columnAmount', { defaultValue: 'Amount' })}</span>
          </div>

          <div className={cn('tableBody')}>
            {mergedRows.map((row) => (
              <div key={row.id} className={cn('tableRow')}>
                <span>{formatTransactionDate(row.date)}</span>

                <CategoryEditor
                  transactionID={row.id}
                  classification={row.classification}
                  side={side}
                  categories={categories}
                  onUpdateCategory={onUpdateCategory}
                />

                <span>{row.description}</span>
                <span>{formatAmount(row.amount, currency)}</span>
              </div>
            ))}

            {mergedRows.length === 0 && (
              <p className={cn('emptyState')}>
                {t('noTransactions', { defaultValue: 'No transactions in this period.' })}
              </p>
            )}
          </div>
        </div>
      </div>
    </SideDrawer>
  );
}
